import React from 'react';

export const ExpandWidgetContext = React.createContext<{
  expandedBy: number;
  setExpandedBy: React.Dispatch<React.SetStateAction<number>>;
}>({ expandedBy: 0, setExpandedBy: () => null });

export const useWidgetExpanderClient = (
  { bonusHeight = 0 }: { bonusHeight?: number } = { bonusHeight: 0 }
) => {
  const { expandedBy, setExpandedBy } = useWidgetExpanderContext();
  const measuredElement = React.useRef() as React.MutableRefObject<HTMLDivElement | null>;
  // Keep callback in a ref to get the updated state value - https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  const savedCallback = React.useRef() as React.MutableRefObject<
    (entries: ResizeObserverEntry[]) => void
  >;

  const elementResizeCallback = (entries: ResizeObserverEntry[]) => {
    const entry = entries[0].target;
    const spaceNeeded =
      entry.getBoundingClientRect().bottom - (window.innerHeight - expandedBy) + bonusHeight;

    if (spaceNeeded > 0 && entry.clientHeight > 0) {
      setExpandedBy(spaceNeeded);
    } else {
      setExpandedBy(0);
    }
  };

  React.useEffect(() => {
    savedCallback.current = elementResizeCallback;
  });

  React.useEffect(() => {
    function resize(entries: ResizeObserverEntry[]) {
      savedCallback.current(entries);
    }
    const HeightObserver = new ResizeObserver(resize);
    if (measuredElement.current) {
      HeightObserver.observe(measuredElement.current);
    }
    return () => {
      if (measuredElement.current) {
        HeightObserver.unobserve(measuredElement.current);
      }
    };
  }, []);

  return measuredElement;
};

export const useWidgetExpanderContext = () => React.useContext(ExpandWidgetContext);
